/**
 * Msg 560 · D.2 — Attachment UI (thumbnails, modals, strip).
 */
import React, { useEffect, useState } from "react";
import {
  Plus, Check, X as CloseIcon, Loader2, Trash2,
  ImageIcon, ListChecks, Paperclip, ExternalLink, Link2,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

import { Chip, humanize } from "./AtlasTopicShared";


function AttachmentThumb({ att, onClick }) {
  const testId = `atlas-attachment-${att.id}`;
  const base = "shrink-0 relative rounded-xl overflow-hidden border border-tb-navy/15 hover:border-tb-navy transition text-left bg-white";
  if (att.type === "photo") return <PhotoThumb att={att} onClick={onClick} testId={testId} base={base} />;
  if (att.type === "pdf") return <PdfThumb att={att} onClick={onClick} testId={testId} base={base} />;
  if (att.type === "link") return <LinkThumb att={att} onClick={onClick} testId={testId} base={base} />;
  if (att.type === "checklist") return <ChecklistThumb att={att} onClick={onClick} testId={testId} base={base} />;
  return null;
}

function PhotoThumb({ att, onClick, testId, base }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId} className={`${base} w-40 h-28 group`}>
      {att.url
        ? <img src={att.url} alt={att.title || "photo"} className="w-full h-full object-cover" />
        : <div className="w-full h-full flex items-center justify-center bg-tb-navy/5"><ImageIcon className="w-6 h-6 text-tb-navy/40" /></div>}
      {att.title && (
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent text-white font-hand text-[11px] px-2 py-1 truncate">
          {att.title}
        </div>
      )}
    </button>
  );
}

function PdfThumb({ att, onClick, testId, base }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId} className={`${base} w-40 h-28 p-3 flex flex-col justify-between`}>
      <div className="inline-flex items-center gap-1.5">
        <Paperclip className="w-4 h-4 text-red-700" />
        <span className="font-hand text-[10px] uppercase tracking-widest text-red-700">PDF</span>
      </div>
      <div className="font-hand text-xs text-tb-navy line-clamp-2">{att.title || "Document"}</div>
    </button>
  );
}

function LinkThumb({ att, onClick, testId, base }) {
  let host = "";
  try { host = new URL(att.url).hostname.replace(/^www\./, ""); } catch { host = ""; }
  const fav = host ? `https://www.google.com/s2/favicons?domain=${host}&sz=64` : null;
  return (
    <button type="button" onClick={onClick} data-testid={testId} className={`${base} w-52 h-16 px-3 flex items-center gap-2.5`}>
      {fav
        ? <img src={fav} alt="" className="w-5 h-5 rounded-sm shrink-0" />
        : <Link2 className="w-4 h-4 text-tb-navy/60 shrink-0" />}
      <div className="min-w-0">
        <div className="font-hand text-xs text-tb-navy truncate">{att.title || host || att.url}</div>
        <div className="font-hand text-[10px] text-tb-navy/50 truncate">{host || "link"}</div>
      </div>
      <ExternalLink className="w-3 h-3 text-tb-navy/40 shrink-0" />
    </button>
  );
}

function ChecklistThumb({ att, onClick, testId, base }) {
  const items = att.checklist_items || [];
  const done  = items.filter((i) => i.checked).length;
  return (
    <button type="button" onClick={onClick} data-testid={testId} className={`${base} w-52 h-28 p-3 flex flex-col justify-between`}>
      <div className="inline-flex items-center gap-1.5">
        <ListChecks className="w-4 h-4 text-emerald-700" />
        <span className="font-hand text-[10px] uppercase tracking-widest text-emerald-700">Checklist</span>
      </div>
      <div className="min-w-0">
        <div className="font-hand text-xs text-tb-navy truncate">{att.title || "Checklist"}</div>
        <div className="font-hand text-[11px] text-tb-navy/60 mt-0.5"><b>{done}</b> of {items.length} checked</div>
      </div>
    </button>
  );
}


function LinkAttachmentModal({ open, onClose, onSubmit }) {
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setUrl(""); setTitle(""); setBusy(false); } }, [open]);
  if (!open) return null;
  const submit = async (e) => {
    e?.preventDefault?.();
    if (!url.trim()) { toast.error("URL is required."); return; }
    setBusy(true);
    try { await onSubmit({ type: "link", url: url.trim(), title: title.trim() || undefined }); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] bg-black/45 flex items-center justify-center p-4"
         data-testid="link-attachment-modal"
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <form onSubmit={submit} className="w-full max-w-sm rounded-2xl bg-white shadow-2xl border border-tb-navy/15">
        <div className="px-5 pt-4 pb-2 border-b border-tb-navy/10">
          <h3 className="text-tb-navy font-semibold text-lg" style={{ fontFamily: '"Playfair Display", serif' }}>Add link</h3>
        </div>
        <div className="p-5 space-y-3">
          <label className="block">
            <span className="font-hand text-[10px] uppercase tracking-widest text-tb-navy/60">URL</span>
            <input value={url} onChange={(e) => setUrl(e.target.value)}
              placeholder="https://…" autoFocus
              data-testid="link-attachment-url"
              className="mt-1 w-full h-10 px-3 rounded-lg border border-tb-navy/25 outline-none font-hand text-sm bg-white text-tb-navy" />
          </label>
          <label className="block">
            <span className="font-hand text-[10px] uppercase tracking-widest text-tb-navy/60">Title (optional)</span>
            <input value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder="Short label"
              data-testid="link-attachment-title"
              className="mt-1 w-full h-10 px-3 rounded-lg border border-tb-navy/25 outline-none font-hand text-sm bg-white text-tb-navy" />
          </label>
        </div>
        <div className="px-5 pb-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="h-9 px-4 rounded-full border border-tb-navy/20 text-tb-navy font-hand text-xs">Cancel</button>
          <button type="submit" disabled={busy}
            data-testid="link-attachment-save"
            className="inline-flex items-center gap-2 h-9 px-4 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-xs disabled:opacity-50">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            Add link
          </button>
        </div>
      </form>
    </div>
  );
}


function ChecklistEditorModal({ open, onClose, onSubmit, initial = null }) {
  const [title, setTitle] = useState("");
  const [items, setItems] = useState([{ text: "", checked: false }]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setTitle(initial?.title || "");
    setItems(initial?.checklist_items?.length
      ? initial.checklist_items.map((i) => ({ id: i.id, text: i.text, checked: !!i.checked }))
      : [{ text: "", checked: false }]);
    setBusy(false);
  }, [open, initial]);
  if (!open) return null;
  const submit = async (e) => {
    e?.preventDefault?.();
    const cleanItems = items
      .map((i) => ({ id: i.id, text: (i.text || "").trim(), checked: !!i.checked }))
      .filter((i) => i.text);
    if (cleanItems.length === 0) { toast.error("At least one item required."); return; }
    setBusy(true);
    try {
      await onSubmit({
        type: "checklist",
        title: title.trim() || undefined,
        checklist_items: cleanItems.map((i) => ({
          ...(i.id ? { id: i.id } : {}), text: i.text, checked: i.checked,
        })),
      });
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] bg-black/45 flex items-center justify-center p-4"
         data-testid="checklist-editor-modal"
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <form onSubmit={submit} className="w-full max-w-md rounded-2xl bg-white shadow-2xl border border-tb-navy/15">
        <div className="px-5 pt-4 pb-2 border-b border-tb-navy/10">
          <h3 className="text-tb-navy font-semibold text-lg" style={{ fontFamily: '"Playfair Display", serif' }}>
            {initial ? "Edit checklist" : "New checklist"}
          </h3>
        </div>
        <div className="p-5 space-y-3">
          <label className="block">
            <span className="font-hand text-[10px] uppercase tracking-widest text-tb-navy/60">Title</span>
            <input value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Pre-boarding for Wonder"
              autoFocus data-testid="checklist-title"
              className="mt-1 w-full h-10 px-3 rounded-lg border border-tb-navy/25 outline-none font-hand text-sm bg-white text-tb-navy" />
          </label>
          <ChecklistEditorItems items={items} setItems={setItems} />
        </div>
        <div className="px-5 pb-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="h-9 px-4 rounded-full border border-tb-navy/20 text-tb-navy font-hand text-xs">Cancel</button>
          <button type="submit" disabled={busy}
            data-testid="checklist-save"
            className="inline-flex items-center gap-2 h-9 px-4 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-xs disabled:opacity-50">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
            Save checklist
          </button>
        </div>
      </form>
    </div>
  );
}

function ChecklistEditorItems({ items, setItems }) {
  return (
    <div className="space-y-1.5" data-testid="checklist-items">
      {items.map((it, idx) => (
        <ChecklistEditorRow key={idx} idx={idx} it={it} setItems={setItems} />
      ))}
      <button type="button"
        onClick={() => setItems((prev) => [...prev, { text: "", checked: false }])}
        data-testid="checklist-add-item"
        className="inline-flex items-center gap-1 h-8 px-3 rounded-full border border-dashed border-tb-navy/25 text-tb-navy/70 font-hand text-xs mt-1">
        <Plus className="w-3 h-3" /> Add item
      </button>
    </div>
  );
}

function ChecklistEditorRow({ idx, it, setItems }) {
  return (
    <div className="flex items-center gap-2">
      <input type="checkbox" checked={it.checked}
        onChange={(e) => setItems((prev) => prev.map((p, i) => i === idx ? { ...p, checked: e.target.checked } : p))}
        data-testid={`checklist-item-check-${idx}`}
        className="accent-tb-navy" />
      <input value={it.text}
        onChange={(e) => setItems((prev) => prev.map((p, i) => i === idx ? { ...p, text: e.target.value } : p))}
        placeholder={`Item ${idx + 1}`}
        data-testid={`checklist-item-text-${idx}`}
        className="flex-1 h-9 px-2.5 rounded-lg border border-tb-navy/20 font-hand text-sm text-tb-navy" />
      <button type="button" onClick={() => setItems((prev) => prev.filter((_, i) => i !== idx))}
        className="h-8 w-8 inline-flex items-center justify-center rounded text-tb-navy/50 hover:bg-tb-navy/5">
        <CloseIcon className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}


function AttachmentTypePicker({ open, onClose, onPick }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[55] bg-black/45 flex items-center justify-center p-4"
         data-testid="attachment-type-picker"
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-sm rounded-2xl bg-white shadow-2xl border border-tb-navy/15 p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-tb-navy font-semibold text-lg" style={{ fontFamily: '"Playfair Display", serif' }}>Add attachment</h3>
          <button type="button" onClick={onClose} className="h-8 w-8 inline-flex items-center justify-center rounded-full hover:bg-tb-navy/5 text-tb-navy/60">
            <CloseIcon className="w-4 h-4" />
          </button>
        </div>
        <div className="space-y-2">
          <PickerRow testId="pick-attachment-photo" icon={ImageIcon} label="Photo" hint="Upload an image (JPG/PNG/WEBP)" onClick={() => onPick("photo")} />
          <PickerRow testId="pick-attachment-link" icon={Link2} label="Link" hint="External URL" onClick={() => onPick("link")} />
          <PickerRow testId="pick-attachment-checklist" icon={ListChecks} label="Checklist" hint="An interactive to-do list" onClick={() => onPick("checklist")} />
          {/* PDFs upload via the "Upload document" button in the folder header —
              that path runs the K1-K5 ingestion pipeline. The old "PDF" row here
              pointed at /uploads/image which rejects non-image content-types. */}
        </div>
      </div>
    </div>
  );
}

function PickerRow({ testId, icon: Icon, label, hint, onClick }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId}
      className="flex items-center gap-3 w-full text-left p-3 rounded-xl border border-tb-navy/15 hover:border-tb-navy hover:bg-tb-navy/[0.02]">
      <div className="h-9 w-9 rounded-full bg-tb-navy/5 flex items-center justify-center">
        <Icon className="w-4 h-4 text-tb-navy" />
      </div>
      <div>
        <div className="font-hand text-sm text-tb-navy">{label}</div>
        <div className="font-hand text-[11px] text-tb-navy/50">{hint}</div>
      </div>
    </button>
  );
}


function AttachmentDetailModal({ open, att, onClose, onUpdate, onDelete }) {
  const [busy, setBusy] = useState(false);
  const [localItems, setLocalItems] = useState(att?.checklist_items || []);
  useEffect(() => { setLocalItems(att?.checklist_items || []); }, [att]);
  if (!open || !att) return null;

  const persistItems = async (nextItems) => {
    setLocalItems(nextItems);
    setBusy(true);
    try {
      await onUpdate({
        checklist_items: nextItems.map((i) => ({ id: i.id, text: i.text, checked: !!i.checked })),
      });
    } catch { toast.error("Update failed."); }
    finally { setBusy(false); }
  };
  const toggle = (id) => persistItems(localItems.map((i) => i.id === id ? { ...i, checked: !i.checked } : i));
  const addItem = () => {
    const text = window.prompt("New item text");
    if (!text || !text.trim()) return;
    persistItems([...localItems, { text: text.trim(), checked: false }]);
  };
  const removeItem = (id) => persistItems(localItems.filter((i) => i.id !== id));

  const deleteAtt = async () => {
    if (!window.confirm(`Delete "${att.title || att.type}"?`)) return;
    setBusy(true);
    try { await onDelete(); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[55] bg-black/60 flex items-center justify-center p-4"
         data-testid="attachment-detail-modal"
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-2xl rounded-3xl bg-white shadow-2xl border border-tb-navy/15 overflow-hidden">
        <AttachmentDetailHeader att={att} busy={busy} onDelete={deleteAtt} onClose={onClose} />
        <div className="p-5 max-h-[70vh] overflow-y-auto">
          <AttachmentDetailBody
            att={att} busy={busy} localItems={localItems}
            onToggle={toggle} onAdd={addItem} onRemove={removeItem}
          />
        </div>
      </div>
    </div>
  );
}

function AttachmentDetailHeader({ att, busy, onDelete, onClose }) {
  return (
    <div className="flex items-center justify-between px-5 py-3 border-b border-tb-navy/10">
      <div className="flex items-center gap-2 min-w-0">
        <Chip tone="ghost">{att.type}</Chip>
        <h3 className="text-tb-navy font-semibold text-lg truncate" style={{ fontFamily: '"Playfair Display", serif' }}>
          {att.title || humanize(att.type)}
        </h3>
      </div>
      <div className="flex items-center gap-1">
        <button type="button" onClick={onDelete} disabled={busy}
          data-testid="attachment-detail-delete"
          className="h-8 w-8 inline-flex items-center justify-center rounded-full text-red-700 hover:bg-red-50">
          <Trash2 className="w-4 h-4" />
        </button>
        <button type="button" onClick={onClose}
          className="h-8 w-8 inline-flex items-center justify-center rounded-full hover:bg-tb-navy/5 text-tb-navy/60">
          <CloseIcon className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

function AttachmentDetailBody({ att, busy, localItems, onToggle, onAdd, onRemove }) {
  if (att.type === "photo") {
    return <img src={att.url} alt={att.title || "photo"} className="w-full max-h-[60vh] object-contain rounded-xl bg-tb-navy/5" />;
  }
  if (att.type === "pdf") {
    return (
      <a href={att.url} target="_blank" rel="noreferrer"
        className="inline-flex items-center gap-2 h-11 px-4 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-sm">
        <Paperclip className="w-4 h-4" /> Open PDF in new tab <ExternalLink className="w-3.5 h-3.5" />
      </a>
    );
  }
  if (att.type === "link") {
    return (
      <a href={att.url} target="_blank" rel="noreferrer"
        className="inline-flex items-center gap-2 h-11 px-4 rounded-full bg-tb-navy text-tb-gold-bright font-hand text-sm">
        <Link2 className="w-4 h-4" /> Open link <ExternalLink className="w-3.5 h-3.5" />
      </a>
    );
  }
  if (att.type === "checklist") {
    return (
      <div className="space-y-1.5" data-testid="attachment-detail-checklist">
        {localItems.map((i) => (
          <ChecklistDetailRow key={i.id} i={i} busy={busy} onToggle={onToggle} onRemove={onRemove} />
        ))}
        <button type="button" onClick={onAdd}
          data-testid="attachment-detail-add-item"
          className="inline-flex items-center gap-1 h-8 px-3 rounded-full border border-dashed border-tb-navy/25 text-tb-navy/70 font-hand text-xs mt-1">
          <Plus className="w-3 h-3" /> Add item
        </button>
      </div>
    );
  }
  return null;
}

function ChecklistDetailRow({ i, busy, onToggle, onRemove }) {
  return (
    <label className="flex items-center gap-3 p-2 rounded-lg hover:bg-tb-navy/[0.02]">
      <input type="checkbox" checked={!!i.checked}
        onChange={() => onToggle(i.id)} disabled={busy}
        data-testid={`attachment-detail-check-${i.id}`}
        className="accent-tb-navy w-4 h-4" />
      <span className={`flex-1 font-hand text-sm ${i.checked ? "line-through text-tb-navy/40" : "text-tb-navy"}`}>{i.text}</span>
      <button type="button" onClick={() => onRemove(i.id)}
        className="h-7 w-7 inline-flex items-center justify-center text-tb-navy/40 hover:text-red-600">
        <CloseIcon className="w-3.5 h-3.5" />
      </button>
    </label>
  );
}


export function AttachmentsStrip({ topicSlug, attachments = [], onChange }) {
  const [picker, setPicker] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [checklistOpen, setChecklistOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = React.useRef(null);
  const [fileType, setFileType] = useState("photo");

  const pushCreate = async (body) => {
    try {
      const r = await api.post(`/atlas/topics/${topicSlug}/attachments`, body);
      onChange?.((prev) => [...(prev || []), r.data.attachment]);
      setPicker(false); setLinkOpen(false); setChecklistOpen(false);
      toast.success("Attachment added.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Add failed.");
    }
  };
  const handlePick = (type) => {
    setPicker(false);
    if (type === "photo") {
      setFileType(type); setTimeout(() => fileRef.current?.click(), 60);
    } else if (type === "link") setLinkOpen(true);
    else if (type === "checklist") setChecklistOpen(true);
  };
  const onFile = async (e) => {
    const f = e.target.files?.[0]; e.target.value = ""; if (!f) return;
    setUploading(true);
    try {
      const form = new FormData(); form.append("file", f);
      const up = await api.post("/uploads/image", form, { headers: { "Content-Type": "multipart/form-data" } });
      const url = up.data.url || up.data.file_url;
      if (!url) throw new Error("upload returned no url");
      await pushCreate({ type: fileType, url, title: f.name });
    } catch { toast.error("Upload failed."); }
    finally { setUploading(false); }
  };
  const updateAtt = async (id, patch) => {
    const r = await api.put(`/atlas/topics/${topicSlug}/attachments/${id}`, patch);
    onChange?.((prev) => (prev || []).map((a) => a.id === id ? r.data.attachment : a));
    if (detail?.id === id) setDetail(r.data.attachment);
    return r.data.attachment;
  };
  const deleteAtt = async (id) => {
    await api.delete(`/atlas/topics/${topicSlug}/attachments/${id}`);
    onChange?.((prev) => (prev || []).filter((a) => a.id !== id));
    toast.success("Attachment deleted.");
    setDetail(null);
  };

  return (
    <div data-testid="attachments-strip">
      <div className="flex items-center gap-3 overflow-x-auto pb-2 -mx-1 px-1">
        {(attachments || []).map((a) => (
          <AttachmentThumb key={a.id} att={a} onClick={() => setDetail(a)} />
        ))}
        <AddAttachmentButton uploading={uploading} onClick={() => setPicker(true)} />
      </div>
      <input ref={fileRef} type="file"
        accept="image/*"
        onChange={onFile} className="hidden"
        data-testid="attachments-file-input" />
      <AttachmentTypePicker open={picker} onClose={() => setPicker(false)} onPick={handlePick} />
      <LinkAttachmentModal open={linkOpen} onClose={() => setLinkOpen(false)} onSubmit={pushCreate} />
      <ChecklistEditorModal open={checklistOpen} onClose={() => setChecklistOpen(false)} onSubmit={pushCreate} />
      <AttachmentDetailModal
        open={!!detail} att={detail}
        onClose={() => setDetail(null)}
        onUpdate={(patch) => updateAtt(detail.id, patch)}
        onDelete={() => deleteAtt(detail.id)}
      />
    </div>
  );
}

function AddAttachmentButton({ uploading, onClick }) {
  return (
    <button type="button" onClick={onClick} disabled={uploading}
      data-testid="attachments-add-btn"
      className="shrink-0 w-40 h-28 rounded-xl border-2 border-dashed border-tb-navy/25 text-tb-navy/60 font-hand text-xs inline-flex flex-col items-center justify-center gap-1 hover:border-tb-navy hover:text-tb-navy hover:bg-tb-navy/[0.02] transition">
      {uploading
        ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</>
        : <><Plus className="w-4 h-4" /> Add attachment</>}
    </button>
  );
}
